<template>
    <modal size="xl" @close="$emit('close')" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <span v-if="earningType">Update Earning Type: </span><span v-if="earningType" class="text-thin text-nowrap">{{ earningType.name }}</span>
            <span v-if="!earningType">Add New Earning Type</span>
        </template>
        <slot>
            <form class="mui-form" @submit.prevent="onSubmit()">
                <div class="form-row">
                    <div class="col">
                        <form-input v-model="formData.name" :errors="errors.name" type="text" label="Name" />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col-6">
                        <form-input v-model="formData.sort_priority" :errors="errors.sort_priority" type="number" label="Sort Priority" />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col">
                        <form-input v-model="formData.type" :errors="errors.type" type="select" label="Type" :options="typeOptions"  />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col">
                        <form-input
                            v-model="formData.designation"
                            :errors="errors.designation"
                            type="select"
                            label="Designation"
                            helper-text="Accrual plans use the earning type with their designation. At most one earning type per designation."
                            :options="designationOptions"
                        />
                    </div>
                </div>

                <div class="form-row mb-2">
                    <div class="col">
                        <form-input
                            v-model="formData.is_overtime"
                            :errors="errors.is_overtime"
                            type="checkbox"
                            label="Is Overtime"
                        />
                    </div>
                </div>

                <div class="form-row mb-2">
                    <div class="col">
                        <form-input
                            v-model="formData.has_tip_credit_claimed"
                            :errors="errors.has_tip_credit_claimed"
                            type="checkbox"
                            label="Has Tip Credit Claimed"
                            helper-text="Does this earning type allow the employer to claim a tip credit? Only applicable to hourly earnings."
                        />
                    </div>
                </div>

                <div class="form-row mb-2">
                    <div class="col">
                        <form-input
                            v-model="formData.is_tips"
                            :errors="errors.is_tips"
                            type="checkbox"
                            label="Is TIPS"
                            helper-text="Is this earning type TIPS?"
                        />
                    </div>
                </div>

                <div class="form-row mb-2 ml-2" v-if="formData.is_tips">
                    <div class="col">
                        <form-input
                            v-model="formData.is_cash_tips"
                            :errors="errors.is_cash_tips"
                            type="checkbox"
                            label="Is Cash TIPS"
                            helper-text="Is this earning type cash TIPS? If no, it means this earning represents TIPS paid to the employee via a paycheck AKA credit card tips."
                        />
                    </div>
                </div>

                <div class="form-row mb-2">
                    <div class="col">
                        <form-input
                            v-model="formData.is_work_suffered"
                            :errors="errors.is_work_suffered"
                            type="checkbox"
                            label="Is Work Suffered"
                            helper-text="Should be unchecked for discretionary bonuses."
                        />
                    </div>
                </div>

                <div class="form-row mb-4">
                    <div class="col">
                        <form-input
                            v-model="formData.is_virtual"
                            :errors="errors.is_virtual"
                            type="checkbox"
                            label="Is Virtual/Imputed Income (e.g. PUCC)"
                            helper-text="Check this box for any income that non-cash compensation as well as cash and credit card TIPS."
                        />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col">
                        <form-input v-model="formData.symmetry_wage_type" :errors="errors.symmetry_wage_type" type="select" label="Symmetry Wage Type" :options="symmetryWageTypeOptions" />
                    </div>
                </div>

                <div class="form-row mb-2" v-if="formData.symmetry_wage_type === 'Regular' && !formData.is_virtual">
                    <div class="col">
                        <form-input
                            v-model="formData.symmetry_wage_type_supplemental_treatment"
                            :errors="errors.symmetry_wage_type_supplemental_treatment"
                            type="checkbox"
                            label="Allow Supplemental Treatment"
                            helper-text="When enabled, payroll processors may elect to withhold this earning at the supplemental rate during payroll processing."
                        />
                    </div>
                </div>

                <div class="form-row" v-if="formData.is_virtual">
                    <div class="col">
                        <form-input v-model="formData.imputed_income_type" :errors="errors.imputed_income_type" type="select" label="Imputed Income Type" :options="imputedIncomeTypeOptions" />
                    </div>
                </div>

                <div class="form-row mb-2">
                    <div class="col">
                        <form-input
                            v-model="formData.allow_multiple_per_company"
                            :errors="errors.allow_multiple_per_company"
                            type="checkbox"
                            label="Allow Multiple Per Company"
                            helper-text="When enabled, a company may have more than one earning of this type."
                        />
                    </div>
                </div>

                <div class="form-row">
                    <div class="col-6">
                        <form-input v-model="formData.w2_box" :errors="errors.w2_box" type="select" label="W2 Box" :options="w2BoxOptions" />
                    </div>
                    <div class="col-6">
                        <form-input v-model="formData.w2_code" :errors="errors.w2_code" type="text" label="W2 Code" />
                    </div>
                </div>

                <p class="mt-4 mb-0">
                    <a href class="text-muted small" @click.prevent="extraOpen = !extraOpen">Danger: Developer Only</a>
                </p>

                <div class="form-row mt-2 mb-2" v-show="extraOpen">
                    <div class="col">
                        <label class="form-label">Fields to Customize</label>
                        <div v-for="option in fieldsToCustomizeOptions" :key="option.value" class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :value="option.value"
                                :id="`ftc_${option.value}`"
                                :disabled="option.disabled"
                                v-model="formData.fields_to_customize"
                            />
                            <label class="form-check-label" :for="`ftc_${option.value}`">{{ option.label }}</label>
                        </div>
                        <div v-if="errors.fields_to_customize" class="invalid-feedback d-block">{{ errors.fields_to_customize }}</div>
                    </div>
                </div>


            </form>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row>
                <button type="button" class="btn btn-block btn-outline-primary" @click.prevent="close()">Cancel</button>
                <button type="button" class="btn btn-block btn-primary" @click.prevent="onSubmit()">Save</button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

export default {
    props: ['earningType', ],
    mixins: [FormMixin, ModalMixin, ],
    computed: {
        typeOptions() {
            return [
                {text: 'Hourly', value: 'hourly'},
                {text: 'Per-unit', value: 'per-unit'},
                {text: 'Amount', value: 'amount'},
            ]
        },
        designationOptions() {
            return [
                {text: '-', value: ''},
                {text: 'Vacation Time Off', value: 'vacation-usage'},
                {text: 'Vacation Payout on Termination', value: 'vacation-payout'},
                {text: 'Sick Time Off', value: 'sick-usage'},
                {text: 'Sick Payout on Termination', value: 'sick-payout'},
                {text: 'Other Time Off', value: 'other-time-off-usage'},
                {text: 'Other Time Off Payout on Termination', value: 'other-time-off-payout'},
            ]
        },
        symmetryWageTypeOptions() {
            if (this.formData.is_virtual) {
                return [
                    {text: '-', value: ''},
                    {text: 'Regular', value: 'Regular'},
                    {text: 'Supplemental', value: 'Supplemental'},
                ]
            }
            return [
                {text: '-', value: ''},
                {text: 'Regular', value: 'Regular'},
                {text: 'Supplemental', value: 'Supplemental'},
                {text: 'Custom', value: 'Custom'},
                /*
                {text: 'Overtime', value: 'Overtime'},
                {text: 'TIPS', value: 'TIPS'},
                {text: 'Previous Period', value: 'PreviousPeriod'},
                {text: 'Previous Period Supplemental', value: 'PreviousPeriodSupplemental'},
                {text: 'FUTA Gross', value: 'FUTAGross'},
                {text: 'SUTA Gross', value: 'SUTAGross'},
                {text: 'SUTA Previous State', value: 'SUTAPreviousState'},
                {text: 'SUTA Predecessor', value: 'SUTAPredecessor'},
                {text: 'RUIA Compensation', value: 'RUIA_Compensation'},
                {text: 'CanadaCanadaLumpsum', value: 'CanadaCanadaLumpsum'},
                {text: 'CanadaEI', value: 'CanadaEI'},
                {text: 'CanadaCPP', value: 'CanadaCPP'},
                {text: 'CanadaBonus', value: 'CanadaBonus'},
                {text: 'CanadaCPP_Bonus', value: 'CanadaCPP_Bonus'},
                {text: 'CanadaEI_Bonus', value: 'CanadaEI_Bonus'},
                {text: 'CanadaQPP', value: 'CanadaQPP'},
                {text: 'CanadaQPP_Bonus', value: 'CanadaQPP_Bonus'},
                {text: 'CanadaQPIP', value: 'CanadaQPIP'},
                {text: 'CanadaQPIP_Bonus', value: 'CanadaQPIP_Bonus'},
                {text: 'CanadaPensionIncome', value: 'CanadaPensionIncome'},
                {text: 'CanadaQuebecSinglePayment', value: 'CanadaQuebecSinglePayment'},
                {text: 'CanadaCommission', value: 'CanadaCommission'},
                {text: 'CanadaWorkersCompensation', value: 'CanadaWorkersCompensation'},
                {text: 'CanadaGroupBenefits', value: 'CanadaGroupBenefits'},
                {text: 'CanadaHealthServiceFund', value: 'CanadaHealthServiceFund'},
                */
            ]
        },
        imputedIncomeTypeOptions() {
            return [
                {text: '-', value: ''},
                {text: 'Custom', value: 'Custom'},
                {text: 'Group Term Life Premiums', value: 'GroupTermLife'},
                {text: 'Domestic Partner Benefits', value: 'DomesticPartner'},
            ]
        },
        w2BoxOptions() {
            return [
                {text: '-', value: null},
                {text: 'Box 12', value: 12},
                {text: 'Box 14', value: 14},
            ]
        },
        fieldsToCustomizeOptions() {
            return [
                {value: 'name', label: 'Name', disabled: false},
                {value: 'type', label: 'Type', disabled: false},
                {value: 'is_work_suffered', label: 'Is Work Suffered', disabled: false},
                {value: 'is_virtual', label: 'Is Virtual', disabled: false},
                {value: 'is_cash_tips', label: 'Is Cash Tips', disabled: !this.formData.is_tips},
                {value: 'symmetry_wage_type_supplemental_treatment', label: 'Supplemental Treatment', disabled: false},
            ]
        },
    },
    data() {
        return {
            formData: this.makeFormData(),
            extraOpen: false,
        }
    },
    watch: {
        'formData.is_virtual'(val) {
            if (!val) {
                this.formData.imputed_income_type = ''
            } else {
                this.formData.symmetry_wage_type_supplemental_treatment = false
            }
        },
        'formData.symmetry_wage_type'(val) {
            if (val !== 'Regular') {
                this.formData.symmetry_wage_type_supplemental_treatment = false
            }
        },
        'formData.is_tips'(val) {
            if (!val) {
                this.formData.fields_to_customize = this.formData.fields_to_customize.filter(f => f !== 'is_cash_tips')
            }
        },
    },
    methods: {
        makeFormData() {
            if (this.earningType) {
                return {
                    name: this.earningType.name,
                    sort_priority: this.earningType.sort_priority,
                    type: this.earningType.type,
                    designation: this.earningType.designation || '',
                    is_overtime: this.earningType.is_overtime,
                    has_tip_credit_claimed: this.earningType.has_tip_credit_claimed,
                    is_tips: this.earningType.is_tips,
                    is_cash_tips: this.earningType.is_cash_tips,
                    is_work_suffered: this.earningType.is_work_suffered,
                    is_virtual: this.earningType.is_virtual,
                    symmetry_wage_type: this.earningType.symmetry_wage_type,
                    symmetry_wage_type_supplemental_treatment: this.earningType.symmetry_wage_type_supplemental_treatment,
                    imputed_income_type: this.earningType.imputed_income_type,
                    allow_multiple_per_company: this.earningType.allow_multiple_per_company,
                    fields_to_customize: this.earningType.fields_to_customize || [],
                    w2_box: this.earningType.w2_box,
                    w2_code: this.earningType.w2_code || '',
                }
            } else {
                return {
                    name: '',
                    sort_priority: 0,
                    type: 'hourly',
                    designation: '',
                    is_overtime: false,
                    has_tip_credit_claimed: false,
                    is_tips: false,
                    is_cash_tips: false,
                    is_work_suffered: false,
                    is_virtual: false,
                    symmetry_wage_type: '',
                    symmetry_wage_type_supplemental_treatment: false,
                    imputed_income_type: '',
                    allow_multiple_per_company: false,
                    fields_to_customize: [],
                    w2_box: null,
                    w2_code: '',
                }
            }
        },
        getFormData() {
            return this.formData
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            let promise
            if (this.earningType) {
                promise = this.$api.put(`/admin/tax-engine/earning-types/${this.earningType.id}`, this.getFormData())
            }
            else {
                promise = this.$api.post(`/admin/tax-engine/earning-types`, this.getFormData())
            }

            this.$store.dispatch('START_LOADING')
            return promise.then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        },
    }
}
</script>
